// ============================================================
//  ADVERTISEMENTS PAGE — ADMIN DASHBOARD
//  Path: src/pages/AdvertisementsPage.tsx
//
//  Admin uploads paid promotions for experts. Payment is
//  handled outside the app — admin uploads after confirming.
//
//  Category is REQUIRED. The admin picks a category; the ad appears
//  under that category's GROUP tab, in this page and in the customer
//  app. Groups are a short list, so the tabs stay few however many
//  categories exist. Tabs are built from the ads themselves, so a
//  group gets its tab the moment an ad falls under it.
//
//  Media upload — quarantine/scanner flow (CHANGED): the browser PUTs
//  the file straight to a short-lived signed URL from
//  generateAdMediaUploadUrl (a QUARANTINE object, not the final ad
//  bucket), then confirmAdMediaUpload waits for the backend's
//  malware scanner to clear it before handing back the real mediaUrl.
//  Previously this uploaded straight to Firebase Storage from the
//  browser (uploadBytesResumable) and never passed through the
//  backend or the scanner at all — see admin.service.js's (backend)
//  generateAdMediaUploadUrl for the full rationale.
//
//  FIX (production-readiness pass): confirmAdMediaUpload's own server-
//  side wait is 12s (AD_MEDIA_SCAN_POLL_TIMEOUT_MS), but the scanner
//  Cloud Run service scales to zero (min-instances=0) and a cold start
//  takes 30-60s (architecture doc section 10.3). That means the FIRST
//  ad upload after any idle period will almost always get a 409 well
//  before the scan even begins — this used to surface as a plain
//  error with no retry, forcing the admin to redo the whole upload
//  even though the file was already sitting safely in quarantine and
//  being scanned. handleFileUpload now retries confirmAdMediaUpload
//  on a 409 (never re-PUTs the file — the object and its ticket
//  already exist), up to CONFIRM_MAX_ATTEMPTS times, and shows a
//  distinct "still scanning" message during that wait so the admin
//  isn't staring at a stalled progress bar with no explanation.
// ============================================================

import React, { useEffect, useState, useRef } from 'react'
import {
  listAdvertisements, createAdvertisement, updateAdvertisement,
  deleteAdvertisement, listAllCategoriesAdmin,
  generateAdMediaUploadUrl, confirmAdMediaUpload,
} from '../services/admin.service'
import { useAuth } from '../context/AuthContext'

interface Category { id: string; name: string; group: string | null }

interface Ad {
  id:        string
  title:     string | null
  caption:   string | null
  mediaUrl:  string
  mediaType: 'image' | 'video'
  isActive:  boolean
  startDate: string | null
  endDate:   string | null
  createdAt: string
  expert:    { id: string; user: { firstName: string | null; lastName: string | null } } | null
  category:  { id: string; name: string; group: string | null } | null
}

const EMPTY_FORM = {
  adType: 'internal' as 'internal' | 'external',
  expertId: '', categoryId: '', title: '', caption: '',
  externalName: '', externalPhone: '', externalLink: '', externalNote: '',
  mediaUrl: '', mediaType: 'image' as 'image' | 'video',
  isActive: false, startDate: '', endDate: '',
}

const AdvertisementsPage: React.FC = () => {
  // FIX (production-readiness pass): this page had NO role check at
  // all, even though its route admits customer_service (App.tsx). Every
  // other page reachable by customer_service (BackgroundChecksPage,
  // CategoriesPage, VehicleVerificationsPage) explicitly restricts
  // mutations to super_admin/admin and shows a "View Only" badge —
  // this one let customer_service fully create, edit, delete and
  // upload media for advertisements with no gate whatsoever.
  const { user } = useAuth()
  const canModify = user?.roles.includes('super_admin') || user?.roles.includes('admin')

  const [ads, setAds]               = useState<Ad[]>([])
  const [categories, setCategories] = useState<Category[]>([])
  const [loading, setLoading]       = useState(true)
  const [activeTab, setActiveTab]   = useState('all')
  const [showForm, setShowForm]     = useState(false)
  const [editingId, setEditingId]   = useState<string | null>(null)
  const [form, setForm]             = useState({ ...EMPTY_FORM })
  const [saving, setSaving]         = useState(false)
  const [uploadProgress, setUploadProgress] = useState<number | null>(null)
  // Only set while retrying confirmAdMediaUpload after a 409 — see the
  // FIX note above the file header for why this retry exists.
  const [scanWaitAttempt, setScanWaitAttempt] = useState<number | null>(null)
  const [error, setError]           = useState('')
  const fileInputRef                = useRef<HTMLInputElement>(null)

  const load = async () => {
    setLoading(true)
    try {
      const [adsRes, catsRes]: any[] = await Promise.all([
        listAdvertisements(),
        listAllCategoriesAdmin(),
      ])
      setAds(adsRes?.data?.data ?? [])
      setCategories(catsRes?.data?.data ?? [])
    } catch { setError('Failed to load') }
    finally { setLoading(false) }
  }

  useEffect(() => { load() }, [])

  // ── Helpers ────────────────────────────────────────────────
  const isExpired = (ad: Ad) =>
    ad.isActive && ad.endDate != null && new Date(ad.endDate) < new Date()

  const isScheduledFuture = (ad: Ad) =>
    ad.isActive && ad.startDate != null && new Date(ad.startDate) > new Date()

  const expiredAds = ads.filter(isExpired)

  // Dynamic tabs: one per category GROUP that has ads (same as the customer
  // app). A category with no group, and older ads saved before category
  // became required, fall under "Other".
  const adTab = (a: Ad) => a.category?.group || 'Other'
  const tabNames = Array.from(new Set(ads.map(adTab)))
  const groups = [
    ...tabNames.filter(n => n !== 'Other').sort((a, b) => a.localeCompare(b)),
    ...(tabNames.includes('Other') ? ['Other'] : []),
  ]

  // Categories grouped by their group for the dropdown, so a long list of
  // categories stays easy to scan (an <optgroup> per group, "Other" last).
  const categoryGroups: [string, Category[]][] = (() => {
    const m = new Map<string, Category[]>()
    categories.forEach(c => {
      const g = c.group || 'Other'
      m.set(g, [...(m.get(g) ?? []), c])
    })
    return Array.from(m.entries())
      .map(([g, cs]) => [g, [...cs].sort((a, b) => a.name.localeCompare(b.name))] as [string, Category[]])
      .sort(([a], [b]) => (a === 'Other' ? 1 : b === 'Other' ? -1 : a.localeCompare(b)))
  })()

  const filteredAds = activeTab === 'all'
    ? ads
    : activeTab === 'expired'
    ? ads.filter(isExpired)
    : ads.filter(a => adTab(a) === activeTab)

  // Two steps: (1) PUT the raw bytes to a short-lived signed URL into
  // the QUARANTINE bucket, tracked 0-90% via XHR progress events
  // (fetch() has no upload-progress API); (2) confirm, which blocks
  // server-side (briefly, bounded) while the malware scanner clears
  // the file — shown as a 95% "almost done" state since no further
  // bytes are moving during that wait. Only a CLEAN result ever
  // produces a mediaUrl this form can save.
  // confirm's own wait is short relative to a cold scanner start (see
  // the FIX note above), so a 409 here is retried with a growing
  // "still scanning" message rather than shown to the admin as a
  // failure — CONFIRM_MAX_ATTEMPTS * (server wait + delay) comfortably
  // covers a 60s cold start.
  const CONFIRM_MAX_ATTEMPTS   = 6
  const CONFIRM_RETRY_DELAY_MS = 2000

  const confirmWithRetry = async (objectKey: string) => {
    for (let attempt = 1; attempt <= CONFIRM_MAX_ATTEMPTS; attempt++) {
      try {
        return await confirmAdMediaUpload(objectKey)
      } catch (e: any) {
        const stillScanning = e?.response?.status === 409
        if (!stillScanning || attempt === CONFIRM_MAX_ATTEMPTS) throw e
        setScanWaitAttempt(attempt)
        await new Promise((resolve) => setTimeout(resolve, CONFIRM_RETRY_DELAY_MS))
      }
    }
    // Unreachable — the loop above always returns or throws — but
    // keeps TypeScript happy about a guaranteed return value.
    throw new Error('Upload could not be confirmed')
  }

  const handleFileUpload = async (file: File) => {
    if (!canModify) return
    setError('')
    setUploadProgress(0)
    try {
      const genRes: any = await generateAdMediaUploadUrl({ fileName: file.name, mimeType: file.type })
      const { uploadUrl, objectKey, maxBytes, mediaType, requiredHeaders } = genRes?.data?.data ?? {}
      if (!uploadUrl || !objectKey) throw new Error('Could not start the upload')
      if (typeof maxBytes === 'number' && file.size > maxBytes) {
        throw new Error(`File is too large — the limit is ${Math.floor(maxBytes / (1024 * 1024))}MB`)
      }

      await new Promise<void>((resolve, reject) => {
        const xhr = new XMLHttpRequest()
        xhr.open('PUT', uploadUrl, true)
        xhr.setRequestHeader('Content-Type', file.type)
        // Cloud Storage V4 signed URL — must be sent unchanged or the
        // PUT is refused with its own 400. See admin.service.js's
        // generateAdMediaUploadUrl for where this comes from.
        Object.entries(requiredHeaders || {}).forEach(([key, value]) => {
          xhr.setRequestHeader(key, String(value))
        })
        xhr.upload.onprogress = (evt) => {
          if (evt.lengthComputable) setUploadProgress(Math.round((evt.loaded / evt.total) * 90))
        }
        xhr.onload = () => (xhr.status >= 200 && xhr.status < 300) ? resolve() : reject(new Error('Upload failed'))
        xhr.onerror = () => reject(new Error('Upload failed'))
        xhr.send(file)
      })

      setUploadProgress(95)
      const confirmRes: any = await confirmWithRetry(objectKey)
      const { mediaUrl, mediaType: confirmedMediaType } = confirmRes?.data?.data ?? {}
      if (!mediaUrl) throw new Error('Upload could not be confirmed')

      setForm(f => ({ ...f, mediaUrl, mediaType: (confirmedMediaType || mediaType || 'image') as 'image' | 'video' }))
      setUploadProgress(null)
      setScanWaitAttempt(null)
    } catch (e: any) {
      const stillScanning = e?.response?.status === 409
      setError(stillScanning
        ? 'The file is taking longer than usual to scan. Please try uploading again in a moment.'
        : (e?.response?.data?.message ?? e?.message ?? 'Upload failed'))
      setUploadProgress(null)
      setScanWaitAttempt(null)
    }
  }

  const handleSubmit = async () => {
    if (!canModify) return
    if (!form.categoryId) return setError('Please select a category — it decides which tab the ad appears under')
    if (form.adType === 'internal' && !form.expertId) return setError('Expert ID is required for internal ads')
    if (form.adType === 'external' && !form.externalName.trim()) return setError('Business/person name is required for external ads')
    if (!form.mediaUrl) return setError('Please upload a media file first')
    setSaving(true)
    setError('')
    try {
      const payload = {
        adType:        form.adType,
        expertId:      form.adType === 'internal' ? (form.expertId || null) : null,
        externalName:  form.adType === 'external' ? (form.externalName || null) : null,
        externalPhone: form.adType === 'external' ? (form.externalPhone || null) : null,
        externalLink:  form.adType === 'external' ? (form.externalLink || null) : null,
        externalNote:  form.adType === 'external' ? (form.externalNote || null) : null,
        categoryId:    form.categoryId || null,
        title:         form.title || null,
        caption:       form.caption || null,
        mediaUrl:      form.mediaUrl,
        mediaType:     form.mediaType,
        isActive:      form.isActive,
        startDate:     form.startDate || null,
        endDate:       form.endDate   || null,
      }
      if (editingId) {
        await updateAdvertisement(editingId, payload)
      } else {
        await createAdvertisement(payload)
      }
      setShowForm(false)
      setEditingId(null)
      setForm({ ...EMPTY_FORM })
      await load()
    } catch (e: any) {
      setError(e?.response?.data?.message ?? 'Save failed')
    } finally { setSaving(false) }
  }

  const handleEdit = (ad: Ad) => {
    if (!canModify) return
    setEditingId(ad.id)
    setForm({
      adType:        (ad as any).adType ?? 'internal',
      expertId:      (ad as any).expert?.id ?? '',
      externalName:  (ad as any).externalName ?? '',
      externalPhone: (ad as any).externalPhone ?? '',
      externalLink:  (ad as any).externalLink ?? '',
      externalNote:  (ad as any).externalNote ?? '',
      categoryId:    ad.category?.id ?? '',
      title:         ad.title ?? '',
      caption:       ad.caption ?? '',
      mediaUrl:      ad.mediaUrl,
      mediaType:     ad.mediaType,
      isActive:      ad.isActive,
      startDate:     ad.startDate ? ad.startDate.slice(0, 10) : '',
      endDate:       ad.endDate   ? ad.endDate.slice(0, 10)   : '',
    })
    setShowForm(true)
  }

  const handleDelete = async (id: string) => {
    if (!canModify) return
    if (!window.confirm('Delete this advertisement?')) return
    try { await deleteAdvertisement(id); await load() }
    catch { setError('Delete failed') }
  }

  const handleToggleActive = async (ad: Ad) => {
    if (!canModify) return
    try { await updateAdvertisement(ad.id, { isActive: !ad.isActive }); await load() }
    catch { setError('Update failed') }
  }

  return (
    <div>
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-white">Advertisements</h1>
          <p className="text-gray-400 text-sm mt-1">
            Paid promotions uploaded after expert confirms payment outside the app
          </p>
        </div>
        {canModify ? (
          <button
            onClick={() => { setShowForm(true); setEditingId(null); setForm({ ...EMPTY_FORM }) }}
            className="px-4 py-2.5 bg-brand-600 hover:bg-brand-500 text-white rounded-xl text-sm font-medium transition-colors flex items-center gap-2"
          >
            + New Advertisement
          </button>
        ) : (
          <span className="px-3 py-1.5 text-xs rounded-full bg-yellow-600/20 text-yellow-400">View Only</span>
        )}
      </div>

      {error && (
        <div className="bg-red-500/10 border border-red-500/30 rounded-xl p-4 mb-4 text-red-400 text-sm">
          {error}
          <button onClick={() => setError('')} className="ml-3 text-red-300 hover:text-white">✕</button>
        </div>
      )}

      {/* Upload / Edit form */}
      {showForm && (
        <div className="bg-surface-card border border-border-light rounded-2xl p-6 mb-6">
          <h2 className="text-lg font-semibold text-white mb-5">
            {editingId ? 'Edit Advertisement' : 'New Advertisement'}
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
          {/* Ad Type toggle */}
          <div className="md:col-span-2">
            <label className="text-xs text-gray-400 mb-2 block">Ad Type *</label>
            <div className="flex gap-3">
              <button type="button"
                onClick={() => setForm(f => ({ ...f, adType: 'internal', externalName: '', externalPhone: '', externalLink: '', externalNote: '' }))}
                className={`flex-1 py-2.5 rounded-xl border text-sm font-medium transition-all ${
                  form.adType === 'internal' ? 'border-brand-500 bg-brand-500/10 text-brand-400' : 'border-border-light bg-surface-tertiary text-gray-400'
                }`}>
                🔵 Internal — Registered Expert
              </button>
              <button type="button"
                onClick={() => setForm(f => ({ ...f, adType: 'external', expertId: '' }))}
                className={`flex-1 py-2.5 rounded-xl border text-sm font-medium transition-all ${
                  form.adType === 'external' ? 'border-orange-500 bg-orange-500/10 text-orange-400' : 'border-border-light bg-surface-tertiary text-gray-400'
                }`}>
                🟠 External — Not Registered
              </button>
            </div>
            {form.adType === 'external' && (
              <p className="text-xs text-orange-400/70 mt-2">
                ⚠️ Customer will see a contact card with "Not on MatchNBook yet" — no in-app profile.
              </p>
            )}
          </div>

            {/* Expert ID — internal only */}
            {form.adType === 'internal' && (
            <div>
              <label className="text-xs text-gray-400 mb-1 block">Expert ID *</label>
              <input type="text" placeholder="Expert ID from expert management"
                value={form.expertId}
                onChange={e => setForm(f => ({ ...f, expertId: e.target.value }))}
                className="w-full bg-surface-tertiary border border-border-light rounded-xl px-4 py-2.5 text-sm text-white focus:border-brand-500 focus:outline-none"
              />
            </div>
            )}

            {/* External fields */}
            {form.adType === 'external' && (<>
            <div>
              <label className="text-xs text-gray-400 mb-1 block">Business / Person Name *</label>
              <input type="text" placeholder="e.g. John's Plumbing"
                value={form.externalName}
                onChange={e => setForm(f => ({ ...f, externalName: e.target.value }))}
                className="w-full bg-surface-tertiary border border-border-light rounded-xl px-4 py-2.5 text-sm text-white focus:border-brand-500 focus:outline-none"
              />
            </div>
            <div>
              <label className="text-xs text-gray-400 mb-1 block">Phone (optional)</label>
              <input type="text" placeholder="+1 416 555 0000"
                value={form.externalPhone}
                onChange={e => setForm(f => ({ ...f, externalPhone: e.target.value }))}
                className="w-full bg-surface-tertiary border border-border-light rounded-xl px-4 py-2.5 text-sm text-white focus:border-brand-500 focus:outline-none"
              />
            </div>
            <div>
              <label className="text-xs text-gray-400 mb-1 block">Link (optional)</label>
              <input type="text" placeholder="https:// or wa.me/..."
                value={form.externalLink}
                onChange={e => setForm(f => ({ ...f, externalLink: e.target.value }))}
                className="w-full bg-surface-tertiary border border-border-light rounded-xl px-4 py-2.5 text-sm text-white focus:border-brand-500 focus:outline-none"
              />
            </div>
            <div>
              <label className="text-xs text-gray-400 mb-1 block">Note (optional)</label>
              <input type="text" placeholder="e.g. Not on MatchNBook yet"
                value={form.externalNote}
                onChange={e => setForm(f => ({ ...f, externalNote: e.target.value }))}
                className="w-full bg-surface-tertiary border border-border-light rounded-xl px-4 py-2.5 text-sm text-white focus:border-brand-500 focus:outline-none"
              />
            </div>
            </>)}

            {/* Category */}
            <div>
              <label className="text-xs text-gray-400 mb-1 block">Category * <span className="text-gray-500">(the ad appears under this category's group tab)</span></label>
              <select value={form.categoryId}
                onChange={e => setForm(f => ({ ...f, categoryId: e.target.value }))}
                className="w-full bg-surface-tertiary border border-border-light rounded-xl px-4 py-2.5 text-sm text-white focus:border-brand-500 focus:outline-none"
              >
                <option value="">Select a category…</option>
                {categoryGroups.map(([group, cats]) => (
                  <optgroup key={group} label={group}>
                    {cats.map((c: Category) => (
                      <option key={c.id} value={c.id}>{c.name}</option>
                    ))}
                  </optgroup>
                ))}
              </select>
            </div>

            {/* Title */}
            <div>
              <label className="text-xs text-gray-400 mb-1 block">Title (optional)</label>
              <input type="text" placeholder="Headline shown on the card"
                value={form.title}
                onChange={e => setForm(f => ({ ...f, title: e.target.value }))}
                className="w-full bg-surface-tertiary border border-border-light rounded-xl px-4 py-2.5 text-sm text-white focus:border-brand-500 focus:outline-none"
              />
            </div>

            {/* Caption */}
            <div>
              <label className="text-xs text-gray-400 mb-1 block">Caption (optional)</label>
              <input type="text" placeholder="Short description"
                value={form.caption}
                onChange={e => setForm(f => ({ ...f, caption: e.target.value }))}
                className="w-full bg-surface-tertiary border border-border-light rounded-xl px-4 py-2.5 text-sm text-white focus:border-brand-500 focus:outline-none"
              />
            </div>

            {/* Start date */}
            <div>
              <label className="text-xs text-gray-400 mb-1 block">Start Date (optional)</label>
              <input type="date"
                value={form.startDate}
                onChange={e => setForm(f => ({ ...f, startDate: e.target.value }))}
                className="w-full bg-surface-tertiary border border-border-light rounded-xl px-4 py-2.5 text-sm text-white focus:border-brand-500 focus:outline-none"
              />
            </div>

            {/* End date */}
            <div>
              <label className="text-xs text-gray-400 mb-1 block">End Date (optional)</label>
              <input type="date"
                value={form.endDate}
                onChange={e => setForm(f => ({ ...f, endDate: e.target.value }))}
                className="w-full bg-surface-tertiary border border-border-light rounded-xl px-4 py-2.5 text-sm text-white focus:border-brand-500 focus:outline-none"
              />
            </div>
          </div>

          {/* Media upload */}
          <div className="mb-4">
            <label className="text-xs text-gray-400 mb-2 block">Media (image or video) *</label>
            {form.mediaUrl ? (
              <div className="flex items-center gap-3 p-3 bg-surface-tertiary rounded-xl border border-border-light">
                {form.mediaType === 'image' ? (
                  <img src={form.mediaUrl} alt="preview" className="w-16 h-16 object-cover rounded-lg" />
                ) : (
                  <video src={form.mediaUrl} className="w-16 h-16 object-cover rounded-lg" />
                )}
                <div className="flex-1">
                  <p className="text-sm text-green-400">✅ {form.mediaType} uploaded</p>
                  <p className="text-xs text-gray-500 truncate">{form.mediaUrl.slice(0, 60)}...</p>
                </div>
                <button onClick={() => setForm(f => ({ ...f, mediaUrl: '', mediaType: 'image' }))}
                  className="text-xs text-red-400 hover:text-red-300">Remove</button>
              </div>
            ) : (
              <div
                onClick={() => fileInputRef.current?.click()}
                className="border-2 border-dashed border-border-light rounded-xl p-8 text-center cursor-pointer hover:border-brand-500 transition-colors"
              >
                {uploadProgress !== null ? (
                  <div>
                    <div className="w-full bg-surface-tertiary rounded-full h-2 mb-2">
                      <div className="h-2 rounded-full bg-brand-500 transition-all" style={{ width: `${uploadProgress}%` }} />
                    </div>
                    <p className="text-sm text-gray-400">
                      {scanWaitAttempt != null
                        ? `Still scanning for safety — this can take up to a minute after a period of inactivity (attempt ${scanWaitAttempt})...`
                        : `Uploading... ${uploadProgress}%`}
                    </p>
                  </div>
                ) : (
                  <>
                    <p className="text-3xl mb-2">📁</p>
                    <p className="text-sm text-gray-400">Click to upload image or video</p>
                    <p className="text-xs text-gray-600 mt-1">JPG, PNG, GIF, MP4, MOV</p>
                  </>
                )}
              </div>
            )}
            <input ref={fileInputRef} type="file" accept="image/*,video/*" className="hidden"
              onChange={e => { const f = e.target.files?.[0]; if (f) handleFileUpload(f) }}
            />
          </div>

          {/* Active toggle */}
          <div className="flex items-center gap-3 mb-5">
            <input type="checkbox" id="isActive" checked={form.isActive}
              onChange={e => setForm(f => ({ ...f, isActive: e.target.checked }))}
              className="w-4 h-4"
            />
            <label htmlFor="isActive" className="text-sm text-gray-300 cursor-pointer">Active — show to customers immediately</label>
          </div>

          {/* Actions */}
          <div className="flex gap-3">
            <button onClick={handleSubmit} disabled={saving || uploadProgress !== null}
              className="px-5 py-2.5 bg-brand-600 hover:bg-brand-500 disabled:opacity-50 text-white rounded-xl text-sm font-medium transition-colors">
              {saving ? 'Saving...' : editingId ? 'Save Changes' : 'Create Advertisement'}
            </button>
            <button onClick={() => { setShowForm(false); setEditingId(null); setError('') }}
              className="px-5 py-2.5 bg-surface-tertiary text-gray-300 hover:text-white rounded-xl text-sm transition-colors">
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* ── Expired ads warning banner ──────────────────────── */}
      {expiredAds.length > 0 && (
        <div className="bg-red-500/10 border border-red-500/30 rounded-2xl p-4 mb-6">
          <div className="flex items-start gap-3">
            <span className="text-xl mt-0.5">⚠️</span>
            <div className="flex-1">
              <p className="text-sm font-semibold text-red-400 mb-1">
                {expiredAds.length} expired ad{expiredAds.length !== 1 ? 's' : ''} still marked as active
              </p>
              <p className="text-xs text-red-300/70 mb-3">
                These ads have passed their end date but are still showing as active.
                Customers will not see them (the public endpoint filters by date) but
                you should deactivate or delete them to keep the dashboard clean.
              </p>
              <div className="flex flex-wrap gap-2">
                {expiredAds.map(ad => (
                  <div key={ad.id} className="flex items-center gap-2 px-3 py-1.5 bg-red-500/10 border border-red-500/20 rounded-lg">
                    <span className="text-xs text-red-300 font-medium">
                      {ad.title || `${ad.expert?.user?.firstName} ${ad.expert?.user?.lastName}`}
                    </span>
                    <span className="text-xs text-red-400/60">
                      expired {new Date(ad.endDate!).toLocaleDateString()}
                    </span>
                    {canModify && (
                      <button
                        onClick={() => handleToggleActive(ad)}
                        className="text-xs text-red-400 hover:text-white underline ml-1"
                      >
                        Deactivate
                      </button>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Category group tabs */}
      <div className="flex gap-2 mb-5 flex-wrap">
        <button onClick={() => setActiveTab('all')}
          className={`px-4 py-2 text-sm font-medium rounded-xl transition-colors ${
            activeTab === 'all' ? 'bg-brand-600 text-white' : 'bg-surface-card border border-border-light text-gray-400 hover:text-white'
          }`}>
          All ({ads.length})
        </button>
        {expiredAds.length > 0 && (
          <button onClick={() => setActiveTab('expired')}
            className={`px-4 py-2 text-sm font-medium rounded-xl transition-colors ${
              activeTab === 'expired' ? 'bg-red-600 text-white' : 'bg-red-500/10 border border-red-500/30 text-red-400 hover:text-white'
            }`}>
            ⚠️ Expired ({expiredAds.length})
          </button>
        )}
        {groups.filter(g => g !== 'all').map(g => (
          <button key={g} onClick={() => setActiveTab(g)}
            className={`px-4 py-2 text-sm font-medium rounded-xl transition-colors ${
              activeTab === g ? 'bg-brand-600 text-white' : 'bg-surface-card border border-border-light text-gray-400 hover:text-white'
            }`}>
            {g} ({ads.filter(a => adTab(a) === g).length})
          </button>
        ))}
      </div>

      {/* Ads list */}
      {loading ? (
        <div className="bg-surface-card border border-border-light rounded-2xl p-12 text-center text-gray-500">Loading...</div>
      ) : filteredAds.length === 0 ? (
        <div className="bg-surface-card border border-border-light rounded-2xl p-12 text-center text-gray-500">
          No advertisements yet. Click "+ New Advertisement" to add one.
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {filteredAds.map(ad => (
            <div key={ad.id} className={`bg-surface-card border rounded-2xl overflow-hidden ${
              isExpired(ad) ? 'border-red-500/40' : 'border-border-light'
            }`}>
              {/* Media preview */}
              <div className="relative h-48 bg-surface-tertiary">
                {ad.mediaType === 'image' ? (
                  <img src={ad.mediaUrl} alt={ad.title ?? 'Ad'} className="w-full h-full object-cover" />
                ) : (
                  <video src={ad.mediaUrl} className="w-full h-full object-cover" controls />
                )}
                {/* Status badge */}
                <div className={`absolute top-3 right-3 px-2.5 py-1 text-xs rounded-full font-medium ${
                  isExpired(ad)
                    ? 'bg-red-500/90 text-white'
                    : isScheduledFuture(ad)
                    ? 'bg-yellow-500/90 text-white'
                    : ad.isActive
                    ? 'bg-green-500/90 text-white'
                    : 'bg-gray-600/90 text-gray-300'
                }`}>
                  {isExpired(ad)
                    ? '🔴 Expired'
                    : isScheduledFuture(ad)
                    ? '🕐 Scheduled'
                    : ad.isActive
                    ? '🟢 Active'
                    : '⚫ Inactive'
                  }
                </div>
              </div>

              {/* Info */}
              <div className="p-4">
                <div className="flex items-start justify-between mb-2">
                  <div>
                    <p className="text-sm font-semibold text-white">{ad.title || 'No title'}</p>
                    <p className="text-xs text-gray-400 mt-0.5">
                      {ad.expert?.user?.firstName} {ad.expert?.user?.lastName}
                    </p>
                  </div>
                  {ad.category ? (
                    <span className="px-2 py-0.5 text-xs rounded-full bg-brand-500/20 text-brand-400 flex-shrink-0 ml-2">
                      {ad.category.name}
                    </span>
                  ) : (
                    <span className="px-2 py-0.5 text-xs rounded-full bg-amber-500/20 text-amber-400 flex-shrink-0 ml-2" title="Edit this ad and select a category">
                      ⚠ No category
                    </span>
                  )}
                </div>
                {ad.caption && <p className="text-xs text-gray-500 mb-3 line-clamp-2">{ad.caption}</p>}
                {(ad.startDate || ad.endDate) && (
                  <p className="text-xs text-gray-600 mb-3">
                    {ad.startDate ? `From ${new Date(ad.startDate).toLocaleDateString()}` : ''}
                    {ad.endDate   ? ` → ${new Date(ad.endDate).toLocaleDateString()}` : ''}
                  </p>
                )}
                {canModify && (
                  <div className="flex gap-2 mt-3">
                    <button onClick={() => handleToggleActive(ad)}
                      className={`flex-1 py-1.5 text-xs rounded-lg font-medium transition-colors ${
                        ad.isActive
                          ? 'bg-gray-600/30 text-gray-300 hover:bg-gray-600/50'
                          : 'bg-green-500/20 text-green-400 hover:bg-green-500/30'
                      }`}>
                      {ad.isActive ? 'Deactivate' : 'Activate'}
                    </button>
                    <button onClick={() => handleEdit(ad)}
                      className="flex-1 py-1.5 text-xs rounded-lg bg-brand-500/20 text-brand-400 hover:bg-brand-500/30 font-medium transition-colors">
                      Edit
                    </button>
                    <button onClick={() => handleDelete(ad.id)}
                      className="py-1.5 px-3 text-xs rounded-lg bg-red-500/20 text-red-400 hover:bg-red-500/30 font-medium transition-colors">
                      Delete
                    </button>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

export default AdvertisementsPage